// API Key React Query Hooks

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useProjectContext } from "../context/ProjectContext";
import { apiKeyService } from "../services/api-keys";
import type { CreateApiKeyDto, UpdateApiKeyDto } from "../types/api-keys";

// Query keys for cache management
export const apiKeyQueryKeys = {
	base: ["api-keys"] as const,
	project: (projectId?: string) => {
		if (projectId) {
			return [...apiKeyQueryKeys.base, projectId] as const;
		}
		return apiKeyQueryKeys.base;
	},
	list: (projectId?: string) =>
		[...apiKeyQueryKeys.project(projectId), "list"] as const,
	detail: (projectId: string | undefined, id: string) =>
		[...apiKeyQueryKeys.project(projectId), "detail", id] as const,
	stats: (projectId: string | undefined, id: string) =>
		[...apiKeyQueryKeys.project(projectId), "stats", id] as const,
	metrics: (projectId: string | undefined, id: string, period: string) =>
		[...apiKeyQueryKeys.project(projectId), "metrics", id, period] as const,
	allMetrics: (projectId: string | undefined, period: string) =>
		[...apiKeyQueryKeys.project(projectId), "all-metrics", period] as const,
};

/**
 * Hook to fetch all API keys for the current user
 */
export const useApiKeys = () => {
	const { selectedProject } = useProjectContext();
	const projectId = selectedProject?.id;

	return useQuery({
		queryKey: apiKeyQueryKeys.list(projectId),
		enabled: Boolean(projectId),
		queryFn: () => apiKeyService.getAll(projectId!),
		staleTime: 1000 * 60, // 1 minute
	});
};

/**
 * Hook to fetch a single API key by ID
 */
export const useApiKey = (keyId: string, projectIdOverride?: string) => {
	const { selectedProject } = useProjectContext();
	const projectId = projectIdOverride ?? selectedProject?.id;

	return useQuery({
		queryKey: apiKeyQueryKeys.detail(projectId, keyId),
		queryFn: () => apiKeyService.getById(projectId!, keyId),
		enabled: Boolean(projectId && keyId),
	});
};

/**
 * Hook to fetch API key statistics
 */
export const useApiKeyStats = (keyId: string, projectIdOverride?: string) => {
	const { selectedProject } = useProjectContext();
	const projectId = projectIdOverride ?? selectedProject?.id;

	return useQuery({
		queryKey: apiKeyQueryKeys.stats(projectId, keyId),
		queryFn: () => apiKeyService.getStats(projectId!, keyId),
		enabled: Boolean(projectId && keyId),
		staleTime: 1000 * 60,
		refetchInterval: 1000 * 60,
	});
};

/**
 * Hook to fetch API key metrics with time-series data
 */
export const useApiKeyMetrics = (
	keyId: string,
	period: "hour" | "day" | "week" | "month" = "day",
	projectIdOverride?: string,
) => {
	const { selectedProject } = useProjectContext();
	const projectId = projectIdOverride ?? selectedProject?.id;

	return useQuery({
		queryKey: apiKeyQueryKeys.metrics(projectId, keyId, period),
		queryFn: () => apiKeyService.getMetrics(projectId!, keyId, period),
		enabled: Boolean(projectId && keyId),
		staleTime: 1000 * 60,
		refetchInterval: 1000 * 60,
	});
};

/**
 * Hook to fetch aggregated metrics for all API keys
 */
export const useAllApiKeyMetrics = (
	period: "hour" | "day" | "week" | "month" = "day",
) => {
	const { selectedProject } = useProjectContext();
	const projectId = selectedProject?.id;

	return useQuery({
		queryKey: apiKeyQueryKeys.allMetrics(projectId, period),
		enabled: Boolean(projectId),
		queryFn: () => apiKeyService.getAllMetrics(projectId!, period),
		staleTime: 1000 * 30,
		refetchInterval: 1000 * 30,
	});
};

const useProjectScopedMutations = () => {
	const { selectedProject } = useProjectContext();
	const projectId = selectedProject?.id;

	const requireProjectId = () => {
		if (!projectId) {
			throw new Error("Select a project to manage API keys.");
		}
		return projectId;
	};

	return { projectId, requireProjectId };
};

/**
 * Hook to create a new API key
 */
export const useCreateApiKey = () => {
	const queryClient = useQueryClient();
	const { requireProjectId, projectId } = useProjectScopedMutations();

	return useMutation({
		mutationFn: (data: CreateApiKeyDto) =>
			apiKeyService.create(requireProjectId(), data),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: apiKeyQueryKeys.list(projectId),
			});
		},
	});
};

/**
 * Hook to update an API key
 */
export const useUpdateApiKey = () => {
	const queryClient = useQueryClient();
	const { requireProjectId, projectId } = useProjectScopedMutations();

	return useMutation({
		mutationFn: ({ keyId, data }: { keyId: string; data: UpdateApiKeyDto }) =>
			apiKeyService.update(requireProjectId(), keyId, data),
		onSuccess: (_, { keyId }) => {
			queryClient.invalidateQueries({
				queryKey: apiKeyQueryKeys.list(projectId),
			});
			queryClient.invalidateQueries({
				queryKey: apiKeyQueryKeys.detail(projectId, keyId),
			});
		},
	});
};

/**
 * Hook to revoke (delete) an API key
 */
export const useRevokeApiKey = () => {
	const queryClient = useQueryClient();
	const { requireProjectId, projectId } = useProjectScopedMutations();

	return useMutation({
		mutationFn: (keyId: string) =>
			apiKeyService.revoke(requireProjectId(), keyId),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: apiKeyQueryKeys.list(projectId),
			});
		},
	});
};

/**
 * Hook to toggle API key active status
 */
export const useToggleApiKey = () => {
	const queryClient = useQueryClient();
	const { requireProjectId, projectId } = useProjectScopedMutations();

	return useMutation({
		mutationFn: ({ keyId, isActive }: { keyId: string; isActive: boolean }) =>
			apiKeyService.toggleActive(requireProjectId(), keyId, isActive),
		onSuccess: (_, { keyId }) => {
			queryClient.invalidateQueries({
				queryKey: apiKeyQueryKeys.list(projectId),
			});
			queryClient.invalidateQueries({
				queryKey: apiKeyQueryKeys.detail(projectId, keyId),
			});
		},
	});
};
